import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import styled from '@emotion/styled';

const readStoredJson = (storage, key, fallback = null) => {
  const rawValue = storage.getItem(key);

  if (rawValue == null || rawValue === '') return fallback;

  try {
    return JSON.parse(rawValue);
  } catch (_) {
    return rawValue;
  }
};

const asObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

export const buildPaymentReturnPayload = (storage, paymentIntent) => {
  const user = asObject(readStoredJson(storage, 'user', {}));
  const userData = asObject(readStoredJson(storage, 'user_data', {}));
  const afterRedirect = asObject(
    readStoredJson(storage, 'after_redirect', {})
  );
  const context = asObject(
    readStoredJson(storage, 'payment_return_context', {})
  );
  const rewardIds = readStoredJson(storage, 'rewards', []);
  const rewardQuantities = readStoredJson(storage, 'reward_quantities', {});

  return {
    token: paymentIntent,
    project_id: context.projectId,
    support_level_id:
      userData.support_level_id || afterRedirect.support_level_id,
    first_name: user.first_name || userData.first_name || afterRedirect.first_name,
    last_name: user.last_name || userData.last_name || afterRedirect.last_name,
    email: user.email || userData.email || afterRedirect.email,
    user_id: user.id,
    customer: readStoredJson(storage, 'payment_intent_customer', null),
    donation_type: Number(readStoredJson(storage, 'donation_type', 0)),
    amount: Number(readStoredJson(storage, 'amount', 0)),
    tips: Number(readStoredJson(storage, 'tips', 0)),
    reward_ids: Array.isArray(rewardIds) ? rewardIds : [],
    reward_quantities: rewardQuantities,
    street: userData.street || afterRedirect.street,
    building_number:
      userData.building_number || afterRedirect.building_number,
    zip_code: userData.zip_code || afterRedirect.zip_code,
    city: userData.city || afterRedirect.city,
    message: userData.message || afterRedirect.message,
    custom_name: userData.custom_name || afterRedirect.custom_name,
    custom_city: userData.custom_city || afterRedirect.custom_city || '',
    custom_message:
      userData.custom_message || afterRedirect.custom_message || '',
    selected_avatar:
      userData.selected_avatar || afterRedirect.selected_avatar || '',
    anonymous: userData.anonymous || afterRedirect.anonymous || false,
    lottery: userData.lottery || afterRedirect.lottery || false,
  };
};

export const buildThankYouPath = (storage, result, payload) => {
  const afterRedirect = asObject(
    readStoredJson(storage, 'after_redirect', {})
  );
  const context = asObject(
    readStoredJson(storage, 'payment_return_context', {})
  );
  const params = new URLSearchParams();
  const title = result.title || context.projectTitle || afterRedirect.projectTitle;
  const projectId = result.project_id || context.projectId;
  const projectUrl = result.url || context.projectUrl;
  const goodies = storage.getItem('goodies');

  if (title) params.set('title', title);
  params.set('amount', String(payload.amount || 0));
  params.set('tips', String(payload.tips || 0));
  if (projectId) params.set('id', projectId);
  if (projectUrl) params.set('url', projectUrl);
  if (goodies) params.set('g', goodies.replace(/,$/, ''));

  return `/thank-you-page?${params.toString()}`;
};

const navigateToResult = (history, path) => {
  const absoluteUrl = `${window.location.origin}${path}`;

  try {
    if (window.top && window.top !== window) {
      window.top.location.href = absoluteUrl;
      return;
    }
  } catch (_) {}

  history.replace(path);
};

const PaymentReturnPage = ({ history, location }) => {
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const requestInFlight = useRef(false);

  useEffect(() => {
    const paymentIntent = new URLSearchParams(location.search).get(
      'payment_intent'
    );

    if (!paymentIntent) {
      setError('Die Zahlungsreferenz fehlt. Bitte kontaktiere den Support.');
      return undefined;
    }

    if (requestInFlight.current) return undefined;

    let isMounted = true;
    requestInFlight.current = true;
    setError(null);

    const payload = buildPaymentReturnPayload(localStorage, paymentIntent);

    fetch('payments/recurring_direct_payment', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((result) => {
        if (!result?.success) {
          throw new Error('Recurring payment could not be completed');
        }

        if (!isMounted) return;

        navigateToResult(
          history,
          buildThankYouPath(localStorage, result, payload)
        );
      })
      .catch(() => {
        if (isMounted) {
          setError(
            'Die Zahlung konnte nicht abgeschlossen werden. Bitte versuche es erneut.'
          );
        }
      })
      .finally(() => {
        requestInFlight.current = false;
      });

    return () => {
      isMounted = false;
    };
  }, [attempt, history, location.search]);

  return (
    <Page>
      <Card>
        {error ? (
          <>
            <Title>Zahlung noch nicht abgeschlossen</Title>
            <Message>{error}</Message>
            <RetryButton type="button" onClick={() => setAttempt((v) => v + 1)}>
              Erneut versuchen
            </RetryButton>
          </>
        ) : (
          <>
            <Spin size="large" />
            <Title>Zahlung wird verarbeitet...</Title>
            <Message>Bitte schließe dieses Fenster nicht.</Message>
          </>
        )}
      </Card>
    </Page>
  );
};

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f7f8f5;
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  padding: 40px 32px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

const Title = styled.h1`
  margin: 20px 0 8px;
  color: #414651;
  font-size: 24px;
`;

const Message = styled.p`
  margin: 0;
  color: #667085;
  font-size: 16px;
`;

const RetryButton = styled.button`
  margin-top: 24px;
  padding: 12px 20px;
  border: 0;
  border-radius: 8px;
  background: #95bd51;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
`;

export default PaymentReturnPage;
